import PropTypes from 'prop-types'
import React from 'react'

import { ReactComponent as CloseIcon } from '@doveconviene/sf-ui/icons/close_circle.svg'
import styled from '@emotion/styled'
import Icon from '../../ui/Icon'
import { BottomDrawerContext } from '../../ui/contexts/BottomDrawerContext'

const StyledTitle = styled.span(({ theme: { colors } }) => ({
  flex: '1 1 auto',
  color: colors.mid_grey,
  fontWeight: '800',
}))

const StyledOverlay = styled.div(({ active, theme: { breakpoints } }) => ({
  display: active ? 'block' : 'none',
  position: 'fixed',
  zIndex: '13',
  left: '0',
  top: '0',
  right: '0',
  bottom: '0',
  overflow: 'hidden',
  background: 'rgba(0, 0, 0, 0.5)',
  [`@media (max-width: ${breakpoints.small})`]: {
    transition: 'display 0.25s',
  },
}))

/**
 * The new "expandable" prop allows to open the Bottom Drawer in fullscreen mode, for example
 * if the BottomDrawer is opened from inside the ExpandableDrawer.
 * Fullscreen mode thus prevents visual conflicts with the other modal.
 */
const getDrawerStyle = (expanded, open, { breakpoints, spaces }) => {
  if (open) {
    if (expanded) {
      return {
        top: 0,
        height: '100%',
        width: '100%',
        borderRadius: 0,
      }
    }
    return {
      borderTopRightRadius: spaces.default * 2,
      borderTopLeftRadius: spaces.default * 2,
      top: spaces.default / 2,
      [`@media (min-width: ${breakpoints.medium})`]: {
        height: spaces.default * 36.5,
        width: spaces.large * 25,
        borderRadius: spaces.default * 2,
      },
    }
  }

  return {
    top: '100%',
  }
}

const StyledRoot = styled.div(({ expanded, open, theme }) => ({
  background: 'white',
  position: 'fixed',
  width: '100%',
  height: '100%',
  zIndex: '14',
  bottom: '0px',
  left: '50%',
  transform: ' translateX(-50%)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  ...getDrawerStyle(expanded, open, theme),
}))

const StyledCloseIcon = styled.div(({ theme }) => ({
  color: theme.colors.primary,
  cursor: 'pointer',
  flex: '0 0 auto',
  width: '24px',
  height: '24px',
}))

const StyledHeader = styled.div(({ theme: { colors } }) => ({
  padding: '24px 16px 24px 24px',
  display: 'flex',
  alignItems: 'center',
  borderBottom: `1px solid ${colors.light_grey}`,
}))

const StyledContent = styled.div(() => ({
  height: '100%',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
}))

const BottomDrawer = ({
  open: isOpen,
  title,
  expanded,
  children,
  onClose,
}) => {
  const [, setContentRef] = React.useContext(BottomDrawerContext)
  const [open, setOpen] = React.useState(isOpen)
  const bottomContentRef = React.useRef(null)
  const onCloseIconClick = React.useCallback(() => {
    onClose()
    setOpen(false)
  }, [])

  React.useLayoutEffect(() => {
    setContentRef(bottomContentRef.current)
  }, [])

  React.useEffect(() => {
    setOpen(isOpen)
  }, [isOpen])

  return (
    <>
      <StyledOverlay active={open} />
      <StyledRoot expanded={expanded} open={open} data-test="BottomDrawer">
        <StyledHeader>
          <StyledTitle>
            { title }
          </StyledTitle>
          <StyledCloseIcon onClick={onCloseIconClick} data-test="BottomDrawerClose">
            <Icon>
              <CloseIcon />
            </Icon>
          </StyledCloseIcon>
        </StyledHeader>
        <StyledContent ref={bottomContentRef}>
          {open ? children : null}
        </StyledContent>
      </StyledRoot>
    </>
  )
}

BottomDrawer.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string,
  children: PropTypes.element,
  expanded: PropTypes.bool,
  onClose: PropTypes.func,
}

BottomDrawer.defaultProps = {
  open: false,
  title: '',
  onClose: () => false,
  expanded: false,
  children: null,
}

BottomDrawer.displayName = 'BottomDrawer'

export default BottomDrawer
